import { FUNNEL, REPEATABLE_STEP, type Screen } from "../routes/registry";

/**
 * ═══ L'ORDRE DES ÉTAPES, ET COMBIEN IL Y EN A ═══
 *
 * Le fil était une CONSTANTE : l'ordre du registre, un écran par entrée. Il devient une
 * liste rangée dans la configuration de l'événement — l'organisateur choisit l'ordre, et
 * peut servir plusieurs fois l'étape « informations » (un formulaire par instance).
 *
 * ⚠️ UNE SEULE CLÉ POUR TOUTE LA LISTE, et pas un rang par étape. Deux étapes ne peuvent
 * donc pas se retrouver au même rang, un déplacement est UNE écriture, et l'ordre se lit
 * d'un coup d'œil en base. C'est aussi ce que fait le gestionnaire des pages du site.
 *
 * ⚠️ ELLE NE SE CASCADE PAS. Comme l'ordre des sections du prototype : la STRUCTURE du
 * parcours est commune à l'événement, seuls les libellés et les contenus se surchargent
 * par langue et par typologie. Un exposant qui traverserait les étapes dans un autre
 * ordre qu'un visiteur rendrait le fil d'Ariane, la reprise et le récapitulatif
 * impossibles à raisonner.
 *
 * ⚠️ ABSENTE, RIEN NE CHANGE : on retombe sur l'ordre déclaré du registre. Un événement
 * qui n'y a jamais touché se comporte exactement comme avant, et une étape ajoutée au
 * produit plus tard apparaît à sa place naturelle plutôt que de disparaître.
 */
export const ORDER_KEY = "FO_MEETING_V2_STEPS_ORDER";

/** La clé de l'étape qu'on peut servir PLUSIEURS FOIS — déclarée par le registre. */
export const REPEATABLE = REPEATABLE_STEP?.key ?? "";

/**
 * Une entrée du fil : l'étape déclarée, et — pour une étape répétable — LAQUELLE de ses
 * instances.
 *
 * ⚠️ L'INSTANCE PORTE UN IDENTIFIANT PROPRE, jamais celui du formulaire qu'elle sert.
 * Rien n'interdit de servir deux fois le même formulaire, et surtout changer le
 * formulaire d'une étape ne doit effacer ni son titre ni sa place.
 */
export type Entry = { key: string; instance?: string };

/** Une étape du fil, telle que les écrans la manipulent. */
export type Step = Screen & { funnel: string; stepName: string; instance?: string };

/**
 * L'ADRESSE ET LES CLÉS D'UNE INSTANCE — et la PREMIÈRE ne porte aucun suffixe.
 *
 * ⚠️ C'EST CE QUI REND LA REPRISE INDOLORE. Tout ce qu'un organisateur a déjà réglé vit
 * sous `FO_MEETING_V2_INFO_*`, et l'étape répond à `/registration/informations` : en
 * suffixant la première instance, on rendrait orphelins son titre, son sous-titre et son
 * formulaire, et on casserait les adresses déjà envoyées. Les instances SUIVANTES, elles,
 * sont neuves — elles peuvent porter leur identifiant sans rien perdre.
 */
export function instancePath(base: string, instance: string | undefined): string {
  return !instance || instance === FIRST_INSTANCE ? base : `${base}/${instance}`;
}

export function instanceKey(base: string, instance: string | undefined): string {
  return !instance || instance === FIRST_INSTANCE ? base : `${base}_${instance}`;
}

/** L'instance que sert une adresse, `undefined` si c'est la première. */
export function instanceOf(pathname: string, base: string): string | undefined {
  if (!pathname.startsWith(`${base}/`)) return undefined;
  const remaining = pathname.slice(base.length + 1);
  return remaining === "" ? undefined : remaining;
}

/**
 * L'ordre du registre, une instance pour l'étape répétable — le fil d'aujourd'hui,
 * exprimé dans le nouveau vocabulaire.
 */
export function defaultEntries(): Entry[] {
  return FUNNEL.map((step) =>
    step.key === REPEATABLE
      ? { key: step.key, instance: FIRST_INSTANCE }
      : { key: step.key },
  );
}

/**
 * ⚠️ LA PREMIÈRE INSTANCE PORTE UN NOM FIXE, et c'est ce qui rend la reprise indolore :
 * les réglages écrits avant cette fonctionnalité (`…_INFO_TITLE`, `…_INFO_FORM_ID`) sont
 * ceux de CETTE instance-là. Un identifiant tiré au hasard aurait rendu orphelin tout ce
 * qu'un organisateur avait déjà réglé.
 */
export const FIRST_INSTANCE = "1";

/** `"registration,informations:1,dates"` → la liste. */
export function readOrder(raw: unknown): Entry[] {
  if (typeof raw !== "string" || raw.trim() === "") return [];
  return raw
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => {
      const [key = "", instance] = token.split(":");
      return instance ? { key, instance } : { key };
    });
}

export function writeOrder(entries: ReadonlyArray<Entry>): string {
  return entries
    .map((e) => (e.instance ? `${e.key}:${e.instance}` : e.key))
    .join(",");
}

/**
 * ═══ LES DÉPENDANCES, ET POURQUOI ═══
 *
 * ⚠️ CE NE SONT PAS DES PRÉFÉRENCES DE MISE EN PAGE : chacune vient d'une donnée que
 * l'étape suivante n'a pas encore. Un ordre qui les enfreint ne PLANTE pas — il produit
 * un écran amputé, une question sans sujet ou une inscription qui ne s'écrit nulle part,
 * et cela ne se voit QUE chez le participant. D'où un refus, avec son motif.
 */
type Rule = {
  /** L'étape contrainte. */
  key: string;
  /** Celle qui doit la précéder. */
  after: string;
  pattern: string;
};

export const RULES: ReadonlyArray<Rule> = [
  {
    key: "articles",
    after: "dates",
    pattern:
      "Un article se vend JOUR PAR JOUR : avant l'étape des dates, la billetterie n'a que le niveau « hors jour » à proposer.",
  },
  {
    key: "conferences",
    after: "company",
    pattern:
      "Le choix des conférences se filtre sur la société du participant, et le serveur refuse une conférence où un collègue de la même société est déjà inscrit.",
  },
  {
    key: "companions",
    after: "typology",
    pattern:
      "Un accompagnant hérite de la typologie du porteur : avant cette étape, il n'y a rien à hériter.",
  },
  {
    key: "companions",
    after: "dates",
    pattern:
      "Un accompagnant hérite des jours du porteur quand on ne lui demande pas les siens.",
  },
];

/** Ce qui ne bouge pas, et le motif qu'on affiche si l'on essaie. */
export const FIXES: ReadonlyArray<{ key: string; place: "debut" | "fin"; pattern: string }> = [
  {
    key: "registration",
    place: "debut",
    pattern:
      "La question de la participation ouvre le parcours : « je ne participe pas » retire tout ce qui suit.",
  },
  {
    key: "confirmation",
    place: "fin",
    pattern:
      "La confirmation est le seul écran qui ENREGISTRE : ailleurs, le parcours demanderait tout sans rien écrire.",
  },
];

/**
 * Ce qui cloche dans un ordre, en clair. Vide = il tient.
 *
 * ⚠️ UNE RÈGLE NE VAUT QUE SI LES DEUX ÉTAPES SONT LÀ. Retirer la société du parcours ne
 * doit pas rendre les conférences impossibles à placer : la dépendance disparaît avec
 * l'étape dont elle dépend.
 */
export function violations(entries: ReadonlyArray<Entry>): string[] {
  const rank = new Map<string, number>();
  entries.forEach((e, i) => {
    if (!rank.has(e.key)) rank.set(e.key, i);
  });

  const dits: string[] = [];

  for (const { key, place, pattern } of FIXES) {
    const at = rank.get(key);
    if (at === undefined) continue;
    const expected = place === "debut" ? 0 : entries.length - 1;
    if (at !== expected) dits.push(pattern);
  }

  for (const { key, after, pattern } of RULES) {
    const a = rank.get(key);
    const b = rank.get(after);
    if (a === undefined || b === undefined) continue;
    if (a < b) dits.push(pattern);
  }

  return [...new Set(dits)];
}

/**
 * Déplacer une entrée d'un cran. Rend la nouvelle liste, ou le MOTIF du refus.
 *
 * ⚠️ ON REFUSE PAR LE RÉSULTAT, PAS PAR UNE LISTE DE CAS. On simule le déplacement et on
 * relit les règles : une règle ajoutée demain est donc respectée sans toucher à cette
 * fonction, et aucun cas particulier ne peut lui échapper.
 */
export function move(
  entries: ReadonlyArray<Entry>,
  index: number,
  direction: -1 | 1,
): { entries: Entry[]; refusal?: undefined } | { entries?: undefined; refusal: string } {
  const target = index + direction;
  if (index < 0 || index >= entries.length || target < 0 || target >= entries.length) {
    return { refusal: "Cette étape est déjà au bout du parcours." };
  }

  const next = [...entries];
  const [entry] = next.splice(index, 1);
  next.splice(target, 0, entry!);

  const [pattern] = violations(next);
  return pattern ? { refusal: pattern } : { entries: next };
}

/**
 * L'ORDRE EFFECTIF, à partir de ce qui est enregistré.
 *
 * ⚠️ IL COMPLÈTE, IL NE FAIT PAS CONFIANCE. Une étape absente de la liste enregistrée est
 * ajoutée à sa place déclarée — c'est ce qui fait qu'une étape ajoutée au produit après
 * coup apparaît au lieu de disparaître — et une entrée qui ne correspond à rien du
 * registre est jetée.
 *
 * ⚠️ ET UN ORDRE DEVENU ILLÉGAL RETOMBE SUR CELUI DU REGISTRE, en entier. Le cas existe :
 * une règle ajoutée après coup, ou une étape retirée du produit. Servir un ordre qu'on
 * sait cassé serait le pire des deux — mieux vaut le parcours d'origine, que le panneau
 * signale.
 */
export function orderedEntries(raw: unknown): Entry[] {
  const declarees = defaultEntries();
  const known = new Set(declarees.map((d) => d.key));
  const saved = readOrder(raw).filter((e) => known.has(e.key));
  if (saved.length === 0) return declarees;

  const result = [...saved];
  declarees.forEach((missing, i) => {
    if (result.some((e) => e.key === missing.key)) return;
    /* Elle s'insère AVANT la première étape qui la suit dans l'ordre déclaré : c'est
       ce qui lui rend sa place plutôt que de la reléguer au bout. */
    const next = new Set(declarees.slice(i + 1).map((d) => d.key));
    const at = result.findIndex((e) => next.has(e.key));
    result.splice(at < 0 ? result.length : at, 0, missing);
  });

  return violations(result).length > 0 ? declarees : result;
}
